import { createElement, type ReactElement } from "react";
import type { CanonicalGlyphName } from "./generated/glyph-data";

export type GlyphSize = 16 | 20 | 24;

export interface GlyphRenderProps<Meaning extends string> {
  /** A governed meaning from the supplied semantic ledger. */
  meaning: Meaning;
  /** 16 for compact utility, 20 for navigation and labels, 24 for framework objects. */
  size?: GlyphSize;
  /** A badge meaning composed over the base glyph's bottom-right corner. */
  badge?: Meaning;
  /** Accessible name for icon-only exceptions. Leave unset when a visible label sits beside the glyph. */
  accessibleName?: string;
  className?: string;
}

export interface GlyphShape {
  readonly markup: string;
  readonly viewBox: string;
}

export type GlyphMeaningMap = Readonly<Record<string, CanonicalGlyphName | undefined>>;
export type GlyphShapeMap = Readonly<Record<CanonicalGlyphName, GlyphShape>>;

function resolveShape(
  meaning: string | undefined,
  glyphByMeaning: GlyphMeaningMap,
  glyphShapes: GlyphShapeMap
): { canonical: CanonicalGlyphName; markup: string; viewBox: string } | undefined {
  if (!meaning) return undefined;
  const canonical = glyphByMeaning[meaning];
  const shape = canonical ? glyphShapes[canonical] : undefined;
  return canonical && shape ? { canonical, markup: shape.markup, viewBox: shape.viewBox } : undefined;
}

/** Render one semantic glyph against the caller's audience-scoped meaning map. */
export function renderGlyph<Meaning extends string>(
  { meaning, size = 20, badge, accessibleName, className }: GlyphRenderProps<Meaning>,
  glyphByMeaning: GlyphMeaningMap,
  glyphShapes: GlyphShapeMap
): ReactElement | null {
  const base = resolveShape(meaning, glyphByMeaning, glyphShapes);
  if (!base) return null;
  const { canonical } = base;
  /* Badge-register geometry is authored in the bottom-right quadrant of the
   * shared canvas so it can compose over a base glyph. Rendered on its own
   * (class.done-when), the mark is framed on that quadrant and the stroke is
   * scaled in user units so its rendered weight matches the family's. */
  const standaloneBadge = canonical.startsWith("badges/");
  const viewBox = standaloneBadge ? "13.5 13.5 10 10" : base.viewBox;
  const strokeWidth = standaloneBadge ? 0.75 : 1.75;
  /* The badge geometry is authored in the release's badge register: a 1.5 stroke
   * mark occupying the bottom-right quadrant of the same 24-unit canvas. The
   * knockout square clears that corner of the base glyph first. */
  const badgeShape = resolveShape(badge, glyphByMeaning, glyphShapes);
  const markup = badgeShape
    ? `${base.markup}<rect x="13" y="13" width="11" height="11" fill="var(--apf-glyph-plate, var(--apf-surface, #fff))" stroke="none"/><g stroke-width="1.5">${badgeShape.markup}</g>`
    : base.markup;
  const label = accessibleName?.trim();
  return createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox,
    width: size,
    height: size,
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "butt",
    strokeLinejoin: "miter",
    strokeMiterlimit: 2.5,
    focusable: "false",
    className,
    role: label ? "img" : undefined,
    "aria-label": label || undefined,
    "aria-hidden": label ? undefined : true,
    "data-glyph": canonical,
    "data-meaning": meaning,
    "data-badge": badgeShape ? badge : undefined,
    dangerouslySetInnerHTML: { __html: markup }
  });
}
